import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import type { RadioApproval } from '@/mocks/radioApprovals';
import { radioApprovalsData, planLabels } from '@/mocks/radioApprovals';
import RejectModal from './components/RejectModal';
import ChangesModal from './components/ChangesModal';
import FrequencyStatusBanner from '@/components/feature/FrequencyStatusBanner';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<RadioApproval[]> {
  return new Promise((resolve, reject) => {
    const delay = 500 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.94) {
        reject(new Error('Timeout al conectar con el servidor. Verificá tu conexión.'));
      } else {
        resolve(radioApprovalsData);
      }
    }, delay);
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `Hace ${days}d`;
  return formatDate(iso);
}

const statusDot: Record<string, string> = {
  pending_approval: 'bg-amber-500',
  approved: 'bg-emerald-500',
  rejected: 'bg-red-500',
  needs_changes: 'bg-sky-500',
  suspended: 'bg-orange-500',
};

const statusLabel: Record<string, string> = {
  pending_approval: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  needs_changes: 'Cambios',
  suspended: 'Suspendida',
};

const planIcon: Record<string, string> = {
  free: 'ri-radio-line',
  pro: 'ri-vip-crown-line',
  premium: 'ri-vip-diamond-line',
};

export default function SuperAdminRadioApprovals() {
  const [approvals, setApprovals] = useState<RadioApproval[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedApproval, setSelectedApproval] = useState<RadioApproval | null>(null);
  const [rejectTarget, setRejectTarget] = useState<RadioApproval | null>(null);
  const [changesTarget, setChangesTarget] = useState<RadioApproval | null>(null);
  const [inlineMsg, setInlineMsg] = useState<string | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((data) => {
        setApprovals(data);
        setLoadingState('ready');
        if (data.length > 0 && !selectedApproval) {
          setSelectedApproval(data[0]);
        }
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showInline = (msg: string) => {
    setInlineMsg(msg);
    setTimeout(() => setInlineMsg(null), 3500);
  };

  const showConfirm = (title: string, text: string, confirmLabel: string) => {
    return Swal.fire({
      icon: 'question',
      title,
      text,
      showCancelButton: true,
      confirmButtonText: confirmLabel,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    });
  };

  const sortedApprovals = useMemo(() => {
    return [...approvals].sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [approvals]);

  const pendingCount = approvals.filter((a) => a.status === 'pending_approval').length;

  // --- Actions ---
  const handleSelect = (a: RadioApproval) => {
    setSelectedApproval(a);
  };

  const handleApprove = (a: RadioApproval) => {
    const expiresDate = a.plan === 'free' ? '7 días' : '30 días';
    const expiresISO = new Date(Date.now() + (a.plan === 'free' ? 7 : 30) * 86400000).toISOString();
    showConfirm(
      `¿Aprobar "${a.radioName}"?`,
      `Se activará la radio y se reservará su frecuencia ${a.plan === 'free' ? 'por 7 días (plan Free). Si no completa el proceso, la frecuencia se liberará automáticamente.' : 'por 30 días. El solicitante recibirá una notificación.'}`,
      'Aprobar y reservar frecuencia',
    ).then((result) => {
      if (result.isConfirmed) {
        const now = new Date().toISOString();
        const updated = { ...a, status: 'approved' as const, reviewedAt: now, reviewedBy: 'Admin Global', frequencyReserved: true, frequencyReservedAt: now, frequencyExpiresAt: expiresISO };
        setApprovals((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
        setSelectedApproval(updated);
        showInline(`"${a.radioName}" aprobada. Frecuencia ${a.frequency} MHz reservada por ${expiresDate}.`);
      }
    });
  };

  const handleRejectClick = (a: RadioApproval) => {
    setRejectTarget(a);
  };

  const handleRejectConfirm = (reason: string) => {
    if (!rejectTarget) return;
    const updated = { ...rejectTarget, status: 'rejected' as const, rejectionReason: reason, reviewedAt: new Date().toISOString(), reviewedBy: 'Admin Global' };
    setApprovals((prev) => prev.map((x) => (x.id === rejectTarget.id ? updated : x)));
    setSelectedApproval(updated);
    showInline(`"${rejectTarget.radioName}" rechazada.`);
    setRejectTarget(null);
  };

  const handleChangesClick = (a: RadioApproval) => {
    setChangesTarget(a);
  };

  const handleChangesConfirm = (comment: string) => {
    if (!changesTarget) return;
    const updated = { ...changesTarget, status: 'needs_changes' as const, changeRequestComment: comment, reviewedAt: new Date().toISOString(), reviewedBy: 'Admin Global' };
    setApprovals((prev) => prev.map((x) => (x.id === changesTarget.id ? updated : x)));
    setSelectedApproval(updated);
    showInline(`Cambios solicitados para "${changesTarget.radioName}".`);
    setChangesTarget(null);
  };

  // --- Loading state ---
  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="flex">
          <SASidebar />
          <main className="flex-1 min-w-0 flex items-center justify-center h-screen">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-2 border-foreground-200 border-t-foreground-800 rounded-full animate-spin"></div>
              <p className="text-foreground-400 text-xs">Cargando solicitudes...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="flex">
          <SASidebar />
          <main className="flex-1 min-w-0 flex items-center justify-center h-screen">
            <div className="flex flex-col items-center gap-4 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-800 font-bold text-sm">Error al cargar</h3>
              <p className="text-foreground-400 text-xs max-w-sm">{errorMsg}</p>
              <button
                onClick={loadData}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-foreground-950 text-background-50 rounded-xl text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-sm"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const canAction = selectedApproval && (selectedApproval.status === 'pending_approval' || selectedApproval.status === 'needs_changes');

  return (
    <div className="min-h-screen bg-background-50">
      <div className="flex h-screen overflow-hidden">
        <SASidebar />

        {/* ── Left Panel: Request list ── */}
        <div className="w-full lg:w-[420px] xl:w-[480px] shrink-0 border-r border-background-200 flex flex-col bg-background-50">
          {/* Header */}
          <div className="shrink-0 px-4 py-4 border-b border-background-100">
            {/* Inline message */}
            {inlineMsg && (
              <div className="mb-2 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-medium">
                <i className="ri-check-line text-emerald-500 text-xs"></i>
                {inlineMsg}
              </div>
            )}
            <div className="flex items-center justify-between mb-1">
              <div>
                <h2 className="text-foreground-950 font-bold text-sm">Solicitudes</h2>
                <p className="text-foreground-400 text-[10px] mt-0.5">
                  {pendingCount} pendientes de {approvals.length}
                </p>
              </div>
              <Link
                to="/sa/dashboard"
                className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-foreground-500 text-sm"></i>
              </Link>
            </div>
            {/* SA-27 Estatus y Exclusividad */}
            <div className="mt-3">
              <FrequencyStatusBanner variant="compact" />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {sortedApprovals.map((a) => {
              const isSelected = selectedApproval?.id === a.id;
              return (
                <div
                  key={a.id}
                  onClick={() => handleSelect(a)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-background-50 ${
                    isSelected
                      ? 'bg-accent-50/70 border-l-[3px] border-l-accent-500'
                      : 'hover:bg-background-50 border-l-[3px] border-l-transparent'
                  }`}
                >
                  {/* Logo */}
                  <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center overflow-hidden shrink-0">
                    <img src={a.logoUrl} alt={a.radioName} className="w-full h-full object-cover" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-semibold truncate ${isSelected ? 'text-foreground-900' : 'text-foreground-800'}`}>
                        {a.radioName}
                      </p>
                      {a.status === 'pending_approval' && (
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot[a.status]} ${a.status === 'pending_approval' ? 'animate-pulse' : ''}`}></span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-foreground-400 text-[10px] font-mono">{a.frequency} MHz</span>
                      <span className="text-foreground-200 text-[9px]">·</span>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${
                        a.plan === 'premium' ? 'text-amber-600' :
                        a.plan === 'pro' ? 'text-sky-600' :
                        'text-foreground-400'
                      }`}>
                        <i className={`${planIcon[a.plan]} text-[9px]`}></i>
                        {planLabels[a.plan]}
                      </span>
                    </div>
                  </div>

                  {/* Time + status dot */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-foreground-300 text-[10px]">{relativeTime(a.submittedAt)}</span>
                    {a.status !== 'pending_approval' && (
                      <span className={`w-2 h-2 rounded-full ${statusDot[a.status]}`}></span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-background-100 px-4 py-2.5">
            <p className="text-foreground-300 text-[10px]">
              {sortedApprovals.length} solicitud{sortedApprovals.length !== 1 ? 'es' : ''}
            </p>
          </div>
        </div>

        {/* ── Right Panel: Detail ── */}
        {/* Desktop: always visible */}
        <div className="hidden lg:flex flex-1 min-w-0 flex-col bg-background-50">
          {selectedApproval ? (
            <DetailPanel
              approval={selectedApproval}
              onApprove={handleApprove}
              onReject={handleRejectClick}
              onRequestChanges={handleChangesClick}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-foreground-400 text-sm font-medium">Seleccioná una solicitud</p>
                <p className="text-foreground-300 text-xs mt-1">Hacé clic en una solicitud para ver el detalle</p>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: overlay when selected */}
        {selectedApproval && (
          <div className="lg:hidden fixed inset-0 z-50 bg-background-50 flex flex-col">
            <DetailPanel
              approval={selectedApproval}
              onApprove={handleApprove}
              onReject={handleRejectClick}
              onRequestChanges={handleChangesClick}
              onBack={() => setSelectedApproval(null)}
              isMobile
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {rejectTarget && (
        <RejectModal
          approval={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={handleRejectConfirm}
        />
      )}

      {changesTarget && (
        <ChangesModal
          approval={changesTarget}
          onClose={() => setChangesTarget(null)}
          onConfirm={handleChangesConfirm}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Detail Panel (shared by desktop right panel + mobile overlay)
   ═══════════════════════════════════════════════════════════ */

function DetailPanel({
  approval,
  onApprove,
  onReject,
  onRequestChanges,
  onBack,
  isMobile,
}: {
  approval: RadioApproval;
  onApprove: (a: RadioApproval) => void;
  onReject: (a: RadioApproval) => void;
  onRequestChanges: (a: RadioApproval) => void;
  onBack?: () => void;
  isMobile?: boolean;
}) {
  const a = approval;
  const canAction = a.status === 'pending_approval' || a.status === 'needs_changes';
  const isPending = a.status === 'pending_approval';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-5 py-4 border-b border-background-100 flex items-center gap-4">
        {isMobile && onBack && (
          <button
            onClick={onBack}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-arrow-left-line text-foreground-600 text-sm"></i>
          </button>
        )}
        <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center overflow-hidden shrink-0">
          <img src={a.logoUrl} alt={a.radioName} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-foreground-950 font-bold text-sm truncate">{a.radioName}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-foreground-400 text-[10px] font-mono">{a.frequency} MHz</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              a.status === 'pending_approval' ? 'bg-amber-50 text-amber-700' :
              a.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
              a.status === 'rejected' ? 'bg-red-50 text-red-600' :
              a.status === 'needs_changes' ? 'bg-sky-50 text-sky-700' :
              'bg-orange-50 text-orange-700'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                a.status === 'pending_approval' ? 'bg-amber-500 animate-pulse' :
                a.status === 'approved' ? 'bg-emerald-500' :
                a.status === 'rejected' ? 'bg-red-500' :
                a.status === 'needs_changes' ? 'bg-sky-500' :
                'bg-orange-500'
              }`}></span>
              {statusLabel[a.status]}
            </span>
          </div>
        </div>

        {/* Quick actions in header — only for pending */}
        {isPending && (
          <div className="hidden sm:flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => onApprove(a)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[11px] font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line text-xs"></i>
              Aprobar
            </button>
            <button
              onClick={() => onReject(a)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 text-white text-[11px] font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-xs"></i>
              Rechazar
            </button>
            <button
              onClick={() => onRequestChanges(a)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-500 text-white text-[11px] font-bold hover:bg-sky-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-xs"></i>
              Cambios
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-5 py-4 space-y-5 max-w-2xl">

          {/* ── 6 Key Fields Grid ── */}
          <div>
            <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-3">Información de la solicitud</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {/* Radio */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Radio</p>
                <p className="text-foreground-800 text-xs font-semibold truncate">{a.radioName}</p>
              </div>
              {/* Frecuencia */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Frecuencia</p>
                <p className="text-foreground-800 text-xs font-bold font-mono">{a.frequency} MHz</p>
              </div>
              {/* Plan */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Plan</p>
                <p className={`text-xs font-bold flex items-center gap-1 ${
                  a.plan === 'premium' ? 'text-amber-600' :
                  a.plan === 'pro' ? 'text-sky-600' :
                  'text-foreground-600'
                }`}>
                  <i className={`${planIcon[a.plan]} text-[10px]`}></i>
                  {planLabels[a.plan]}
                </p>
              </div>
              {/* Responsable */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Responsable</p>
                <p className="text-foreground-800 text-xs font-semibold truncate">{a.ownerName}</p>
              </div>
              {/* Contrato */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Contrato</p>
                <p className="text-foreground-800 text-xs font-bold font-mono">{a.contractNumber}</p>
              </div>
              {/* Pago inicial */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Pago inicial</p>
                <p className={`text-xs font-bold ${a.initialPayment > 0 ? 'text-emerald-600' : 'text-foreground-400'}`}>
                  {a.initialPayment > 0
                    ? `${a.initialPaymentCurrency} ${a.initialPayment.toLocaleString('es-ES')}`
                    : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* ── Additional info ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="bg-background-50 border border-background-200 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Categoría</p>
              <p className="text-foreground-800 text-xs font-semibold">{a.category}</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Ubicación</p>
              <p className="text-foreground-800 text-xs font-semibold truncate">{a.city}, {a.country}</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Solicitado</p>
              <p className="text-foreground-800 text-xs font-semibold">{formatDate(a.submittedAt)}</p>
            </div>
          </div>

          {/* ── Stream URL ── */}
          <div>
            <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2">Stream URL</p>
            <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-2">
              <code className="text-foreground-600 text-[11px] font-mono break-all flex-1">{a.streamUrl}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(a.streamUrl);
                }}
                className="shrink-0 w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
                title="Copiar URL"
              >
                <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
              </button>
            </div>
          </div>

          {/* ── Branding ── */}
          <div>
            <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2">Branding</p>
            <div className="bg-background-50 border border-background-200 rounded-xl p-3 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {a.brandingColors.map((color, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-md border border-background-300" style={{ backgroundColor: color }}></div>
                    <span className="text-foreground-500 text-[10px] font-mono">{color}</span>
                  </div>
                ))}
              </div>
              <p className="text-foreground-600 text-[11px] leading-relaxed">{a.brandingDescription}</p>
            </div>
          </div>

          {/* ── Description ── */}
          <div>
            <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2">Descripción</p>
            <p className="text-foreground-600 text-xs leading-relaxed bg-background-50 border border-background-200 rounded-xl p-3">
              {a.description}
            </p>
          </div>

          {/* ── Frequency reserved ── */}
          {a.frequencyReserved && (
            <div>
              <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2">Frecuencia Reservada</p>
              <div className="bg-accent-50/50 border border-accent-200/60 rounded-xl p-3 flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="text-accent-800 text-xs font-bold">{a.frequency} MHz</p>
                  <p className="text-accent-600 text-[10px]">
                    Reservada el {a.frequencyReservedAt ? formatDate(a.frequencyReservedAt) : '—'}
                  </p>
                  {a.frequencyExpiresAt && (
                    <p className={`text-[10px] font-medium ${new Date(a.frequencyExpiresAt) < new Date() ? 'text-red-500' : 'text-accent-500'}`}>
                      {new Date(a.frequencyExpiresAt) < new Date() ? 'Vencida el ' : 'Vence el '}
                      {formatDate(a.frequencyExpiresAt)}
                    </p>
                  )}
                </div>
                <div className="w-9 h-9 rounded-full bg-accent-200/60 flex items-center justify-center">
                  <i className="ri-radar-line text-accent-600 text-base"></i>
                </div>
              </div>
            </div>
          )}

          {/* ── Rejection reason ── */}
          {a.rejectionReason && (
            <div>
              <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
                <i className="ri-error-warning-line text-red-500 text-[10px]"></i>
                Motivo de Rechazo
              </p>
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-700 text-[11px] leading-relaxed">{a.rejectionReason}</p>
              </div>
            </div>
          )}

          {/* ── Changes comment ── */}
          {a.changeRequestComment && (
            <div>
              <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
                <i className="ri-edit-line text-sky-500 text-[10px]"></i>
                Cambios Solicitados
              </p>
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
                <p className="text-sky-700 text-[11px] leading-relaxed">{a.changeRequestComment}</p>
              </div>
            </div>
          )}

          {/* ── Suspension reason ── */}
          {a.suspensionReason && (
            <div>
              <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
                <i className="ri-pause-circle-line text-orange-500 text-[10px]"></i>
                Motivo de Suspensión
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                <p className="text-orange-700 text-[11px] leading-relaxed">{a.suspensionReason}</p>
              </div>
            </div>
          )}

          {/* ── Review info ── */}
          {a.reviewedAt && (
            <div className="text-foreground-300 text-[10px] flex items-center gap-1 pt-2 border-t border-background-200">
              <i className="ri-check-double-line text-[11px]"></i>
              {statusLabel[a.status]} por {a.reviewedBy} el {formatDateTime(a.reviewedAt)}
            </div>
          )}

          {/* Spacer for mobile so buttons aren't cut off */}
          <div className="h-4"></div>
        </div>
      </div>

      {/* ── Action bar (bottom) ── */}
      {canAction && (
        <div className="shrink-0 border-t border-background-200 px-5 py-3 bg-background-50">
          <div className="flex items-center gap-2 max-w-2xl">
            {/* Mobile: show all 3 buttons here */}
            <div className="flex sm:hidden items-center gap-2 w-full">
              <button
                onClick={() => onApprove(a)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-double-line text-sm"></i>
                Aprobar
              </button>
              <button
                onClick={() => onReject(a)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-sm"></i>
                Rechazar
              </button>
              <button
                onClick={() => onRequestChanges(a)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-sky-500 text-white text-xs font-bold hover:bg-sky-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line text-sm"></i>
                Cambios
              </button>
            </div>
            {/* Desktop: actions already in header, show subtle note */}
            <div className="hidden sm:block">
              <p className="text-foreground-400 text-[10px]">
                Usá los botones superiores para aprobar, rechazar o solicitar cambios.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}